/**
 * Application Logger
 * Centralized logging utility for client-side error tracking and debugging
 */

// Log levels
const LogLevel = {
  ERROR: 'error',
  WARN: 'warn', 
  INFO: 'info',
  DEBUG: 'debug'
}

// Configuration
const config = {
  // Enable console logging in development
  enableConsole: import.meta.env.DEV,
  
  // Enable remote logging in production
  enableRemote: import.meta.env.PROD,
  
  // Log level threshold
  logLevel: import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.INFO,
  
  // Remote logging endpoint (would be configured for production)
  remoteEndpoint: '/api/logs'
}

/**
 * Logger class with multiple output targets
 */
class Logger {
  constructor() {
    this.sessionId = this.generateSessionId()
    this.userId = null
    this.context = {}
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  /**
   * Set user context for logs
   */
  setUser(user) {
    this.userId = user?.id
    this.context.user = {
      id: user?.id,
      username: user?.username
    }
  }

  /**
   * Set additional context
   */
  setContext(key, value) {
    this.context[key] = value
  }

  /**
   * Create log entry with metadata
   */
  createLogEntry(level, message, data = {}) {
    return {
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      level,
      message,
      data,
      context: this.context,
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      // App-specific data
      app: {
        name: 'SurviveIt',
        version: '1.0.0',
        environment: import.meta.env.MODE
      }
    }
  }

  /**
   * Check if log level should be processed
   */
  shouldLog(level) {
    const levels = {
      [LogLevel.ERROR]: 0,
      [LogLevel.WARN]: 1,
      [LogLevel.INFO]: 2,
      [LogLevel.DEBUG]: 3
    }
    
    return levels[level] <= levels[config.logLevel]
  }

  /**
   * Output to console (development)
   */
  logToConsole(entry) {
    if (!config.enableConsole) return

    const { level, message, data } = entry
    const style = {
      [LogLevel.ERROR]: 'color: #ef4444; font-weight: bold',
      [LogLevel.WARN]: 'color: #f59e0b; font-weight: bold', 
      [LogLevel.INFO]: 'color: #3b82f6',
      [LogLevel.DEBUG]: 'color: #6b7280'
    }

    console.groupCollapsed(`%c[${level.toUpperCase()}] ${message}`, style[level])
    if (Object.keys(data).length > 0) {
      console.log('Data:', data)
    }
    console.log('Full Entry:', entry)
    console.groupEnd()
  }

  /**
   * Send to remote logging service (production)
   */
  async logToRemote(entry) {
    if (!config.enableRemote || typeof window === 'undefined') return

    try {
      // In production, this would send to your logging service
      // Example: Sentry, LogRocket, DataDog, or custom endpoint
      
      // For now, just store in localStorage as backup
      const logs = JSON.parse(localStorage.getItem('surviveit_logs') || '[]')
      logs.push(entry)
      
      // Keep only last 100 logs to prevent storage bloat
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100)
      }
      
      localStorage.setItem('surviveit_logs', JSON.stringify(logs))

      // TODO: Implement actual remote logging
      // await fetch(config.remoteEndpoint, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(entry)
      // })
      
    } catch (error) {
      console.error('Failed to send log to remote service:', error)
    }
  }

  /**
   * Main logging method
   */
  async log(level, message, data = {}) {
    if (!this.shouldLog(level)) return

    const entry = this.createLogEntry(level, message, data)
    
    // Output to console
    this.logToConsole(entry)
    
    // Send to remote service
    await this.logToRemote(entry)

    return entry
  }

  /**
   * Convenience methods for different log levels
   */
  error(message, data = {}) {
    return this.log(LogLevel.ERROR, message, data)
  }

  warn(message, data = {}) {
    return this.log(LogLevel.WARN, message, data)
  }

  info(message, data = {}) {
    return this.log(LogLevel.INFO, message, data)
  }

  debug(message, data = {}) {
    return this.log(LogLevel.DEBUG, message, data)
  }

  /**
   * Log API errors specifically
   */
  apiError(endpoint, error, requestData = {}) {
    return this.error('API Error', {
      endpoint,
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack
      },
      requestData,
      type: 'api_error'
    })
  }

  /**
   * Log user actions for analytics
   */
  userAction(action, data = {}) {
    return this.info('User Action', {
      action,
      ...data,
      type: 'user_action'
    })
  }

  /**
   * Log performance metrics
   */
  performance(metric, value, data = {}) {
    return this.info('Performance Metric', {
      metric,
      value,
      ...data,
      type: 'performance'
    })
  }

  /**
   * Export logs for debugging
   */
  exportLogs() {
    if (typeof window === 'undefined') return
    
    const logs = JSON.parse(localStorage.getItem('surviveit_logs') || '[]')
    const dataStr = JSON.stringify(logs, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    
    const link = document.createElement('a')
    link.href = URL.createObjectURL(dataBlob)
    link.download = `surviveit-logs-${new Date().toISOString().split('T')[0]}.json`
    link.click()
  }

  /**
   * Clear stored logs
   */
  clearLogs() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('surviveit_logs')
    }
    this.info('Logs cleared')
  }
}

// Create singleton instance
const logger = new Logger()

// Global error handler (only in browser)
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('Global Error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      type: 'global_error'
    })
  })

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled Promise Rejection', {
      reason: event.reason,
      promise: event.promise,
      type: 'promise_rejection'
    })
  })
}

export default logger
export { LogLevel }